import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ActiveSession } from '@/hooks/useDashboardData';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow, differenceInDays } from 'date-fns';
import { de } from 'date-fns/locale';
import { ArrowRight, Plus, Sparkles, Search, ChevronDown, ChevronUp, Users, SlidersHorizontal, Shield, Eye } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { SessionSort, SortOptions } from '@/components/dashboard/SessionSort';
import { SessionFilters as SessionFiltersComponent, SessionFilters as SessionFiltersType } from '@/components/dashboard/SessionFilters';
import { useSessionFilters } from '@/hooks/useSessionFilters';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface ActiveSessionsListProps {
  sessions: ActiveSession[];
  showEmptyState?: boolean;
  onNewDecision?: () => void;
}

type CommitStatus = 'go' | 'analyse' | 'offen' | 'inaktiv';

function getCommitStatus(session: ActiveSession): CommitStatus {
  if (session.subphase === '3.4') return 'go';
  const daysSinceUpdate = differenceInDays(new Date(), new Date(session.updated_at));
  if (daysSinceUpdate > 30) return 'inaktiv';
  if (session.phase >= 2) return 'analyse';
  return 'offen';
}

const statusConfig: Record<CommitStatus, { label: string; dot: string; badge: string }> = {
  go: {
    label: 'GO',
    dot: 'bg-emerald-500',
    badge: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20',
  },
  analyse: {
    label: 'Analyse',
    dot: 'bg-amber-500',
    badge: 'bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20',
  },
  offen: {
    label: 'Offen',
    dot: 'bg-destructive',
    badge: 'bg-destructive/10 text-destructive border-destructive/20',
  },
  inaktiv: {
    label: 'Inaktiv',
    dot: 'bg-muted-foreground/40',
    badge: 'bg-muted/50 text-muted-foreground border-border',
  },
};

const phaseLabels: Record<string, string> = {
  '1.1': 'Problem definieren',
  '1.2': 'Konflikte',
  '1.3': 'Hypothesen',
  '1.4': 'Annahmen',
  '2.1': 'Optionen',
  '2.2': 'Szenarien',
  '2.3': 'Vergleich',
  '2.4': 'Empfehlung',
  '3.1': 'Matrix',
  '3.2': 'Validierung',
  '3.3': 'Commit',
  '3.4': 'Abgeschlossen',
};

function getPhaseLabel(subphase: string): string {
  return phaseLabels[subphase] || `Phase ${subphase}`;
}

function getDecisionTitle(session: ActiveSession): string {
  if (session.summary) return session.summary;
  if (session.decision_type && session.decision_subtype) {
    return `${session.decision_type} – ${session.decision_subtype}`;
  }
  return session.decision_type || 'Neue Entscheidung';
}

function getAge(dateStr: string): string {
  try {
    return formatDistanceToNow(new Date(dateStr), { locale: de });
  } catch {
    return '–';
  }
}

function isActionable(role: string): boolean {
  return role === 'owner' || role === 'contributor';
}

interface SessionRowProps {
  session: ActiveSession;
  dimmed?: boolean;
}

const SessionRow = ({ session, dimmed = false }: SessionRowProps) => {
  const navigate = useNavigate();
  const status = getCommitStatus(session);
  const config = statusConfig[status];
  const isTeam = session.participant_count > 1;

  return (
    <div
      className={`group flex flex-col sm:grid sm:grid-cols-[1fr_100px_90px_120px_80px_80px] gap-1 sm:gap-2 items-start sm:items-center px-3 sm:px-4 py-3 sm:py-2.5 hover:bg-muted/30 transition-colors cursor-pointer ${dimmed ? 'opacity-60 hover:opacity-80' : ''}`}
      onClick={() => navigate(`/app/session/${session.id}`)}
    >
      <div className="flex items-center gap-2 min-w-0 w-full">
        <span className={`text-sm font-medium truncate group-hover:text-primary transition-colors ${dimmed ? 'text-muted-foreground' : 'text-foreground'}`}>
          {getDecisionTitle(session)}
        </span>
        {isTeam && (
          <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground shrink-0" title={`${session.participant_count} Teilnehmer${session.owner_name ? ` · Owner: ${session.owner_name}` : ''}`}>
            <Users className="h-3 w-3" />
            {session.participant_count}
          </span>
        )}
      </div>

      <div className="flex items-center gap-1.5">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${config.dot}`} />
        <Badge variant="outline" className={`text-[10px] sm:text-xs border ${config.badge} px-1.5`}>
          {config.label}
        </Badge>
      </div>

      <Badge variant="secondary" className="text-[10px] sm:text-xs px-1.5 capitalize">
        {session.my_role}
      </Badge>

      <span className="text-xs text-muted-foreground truncate">
        {getPhaseLabel(session.subphase)}
      </span>

      <span className="text-xs text-muted-foreground tabular-nums">
        {getAge(session.created_at)}
      </span>

      <div className="hidden sm:flex justify-end">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-primary/10 hover:text-primary"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/app/session/${session.id}`);
          }}
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export const ActiveSessionsList = ({ sessions, showEmptyState = true, onNewDecision }: ActiveSessionsListProps) => {
  const [isExpandedOwned, setIsExpandedOwned] = useState(false);
  const [isExpandedParticipant, setIsExpandedParticipant] = useState(false);
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<CommitStatus | 'all'>('all');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<SessionFiltersType>({
    phase: 'all',
    decisionArea: 'all',
    dateRange: 'all',
    searchQuery: '',
  });
  const [sortOptions, setSortOptions] = useState<SortOptions>({
    field: 'date',
    direction: 'desc',
  });
  const maxVisiblePerGroup = 5;

  const roleFilters = [
    { value: 'all', label: 'Alle' },
    { value: 'owner', label: 'Owner' },
    { value: 'contributor', label: 'Contributor' },
    { value: 'reviewer', label: 'Reviewer' },
    { value: 'viewer', label: 'Viewer' },
  ];

  const statusFilters: { value: CommitStatus | 'all'; label: string; dot?: string }[] = [
    { value: 'all', label: 'Alle' },
    { value: 'go', label: 'GO', dot: statusConfig.go.dot },
    { value: 'analyse', label: 'Analyse', dot: statusConfig.analyse.dot },
    { value: 'offen', label: 'Offen', dot: statusConfig.offen.dot },
    { value: 'inaktiv', label: 'Inaktiv', dot: statusConfig.inaktiv.dot },
  ];

  // Apply role + status filter first
  const roleFiltered = sessions.filter(s => {
    if (roleFilter !== 'all' && s.my_role !== roleFilter) return false;
    if (statusFilter !== 'all' && getCommitStatus(s) !== statusFilter) return false;
    return true;
  });

  // Apply advanced filters + sort via hook
  const { filteredSessions, activeFilterCount } = useSessionFilters(roleFiltered, filters, sortOptions);

  // Split into two groups
  const { ownedSessions, participantSessions } = useMemo(() => {
    const owned: ActiveSession[] = [];
    const participant: ActiveSession[] = [];
    for (const s of filteredSessions) {
      if (isActionable(s.my_role)) {
        owned.push(s);
      } else {
        participant.push(s);
      }
    }
    return { ownedSessions: owned, participantSessions: participant };
  }, [filteredSessions]);

  const visibleOwned = isExpandedOwned ? ownedSessions : ownedSessions.slice(0, maxVisiblePerGroup);
  const hasMoreOwned = ownedSessions.length > maxVisiblePerGroup;

  const visibleParticipant = isExpandedParticipant ? participantSessions : participantSessions.slice(0, maxVisiblePerGroup);
  const hasMoreParticipant = participantSessions.length > maxVisiblePerGroup;

  const handleSearchChange = (value: string) => {
    setFilters(prev => ({ ...prev, searchQuery: value }));
  };

  if (sessions.length === 0 && showEmptyState) {
    return (
      <div className="rounded-2xl border border-border/50 bg-card p-8 text-center">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-6 h-6 text-primary" />
        </div>
        <h3 className="text-lg font-semibold text-foreground mb-2">Keine Entscheidungen</h3>
        <p className="text-sm text-muted-foreground mb-4">Starte deine erste strukturierte Entscheidung.</p>
        {onNewDecision && (
          <Button onClick={onNewDecision} className="shadow-lg shadow-primary/20">
            <Plus className="h-4 w-4 mr-1.5" />
            Neue Entscheidung
          </Button>
        )}
      </div>
    );
  }

  if (sessions.length === 0) {
    return (
      <div className="rounded-2xl border border-border/50 bg-card p-6 text-center">
        <p className="text-sm text-muted-foreground">Keine Entscheidungen gefunden.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Shared controls card */}
      <div className="rounded-2xl border border-border/50 bg-card overflow-hidden">
        <div className="flex flex-col gap-2 p-3 sm:p-4">
          <div className="flex items-center gap-3">
            <h3 className="text-sm sm:text-base font-semibold text-foreground whitespace-nowrap">Commitments</h3>
            <span className="text-xs text-muted-foreground tabular-nums">{filteredSessions.length}</span>
            <div className="flex-1" />
            <div className="relative max-w-[200px] sm:max-w-[240px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Suchen..."
                value={filters.searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="h-8 pl-8 text-xs bg-muted/30 border-border/50"
              />
            </div>
            <SessionSort sortOptions={sortOptions} onSortChange={setSortOptions} />
          </div>

          {/* Role filter chips */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {roleFilters.map(rf => (
              <Button
                key={rf.value}
                variant={roleFilter === rf.value ? 'default' : 'outline'}
                size="sm"
                className="h-6 text-[10px] sm:text-xs px-2"
                onClick={() => setRoleFilter(rf.value)}
              >
                {rf.label}
              </Button>
            ))}
            <div className="flex-1" />
            <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 text-[10px] sm:text-xs px-2 gap-1 text-muted-foreground hover:text-primary">
                  <SlidersHorizontal className="h-3 w-3" />
                  Filter
                  {activeFilterCount > 0 && (
                    <Badge variant="secondary" className="h-4 text-[9px] px-1 ml-0.5">{activeFilterCount}</Badge>
                  )}
                </Button>
              </CollapsibleTrigger>
            </Collapsible>
          </div>

          {/* Status filter badges */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {statusFilters.map(sf => (
              <button
                key={sf.value}
                onClick={() => setStatusFilter(sf.value)}
                className={`inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full text-[10px] sm:text-xs font-medium border transition-colors ${
                  statusFilter === sf.value
                    ? sf.value === 'all'
                      ? 'bg-primary text-primary-foreground border-primary'
                      : `${statusConfig[sf.value].badge} ring-1 ring-offset-1 ring-offset-background ring-current`
                    : 'bg-muted/30 text-muted-foreground border-border/50 hover:bg-muted/50'
                }`}
              >
                {sf.dot && <span className={`w-1.5 h-1.5 rounded-full ${sf.dot}`} />}
                {sf.label}
              </button>
            ))}
          </div>

          {/* Collapsible advanced filters */}
          <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
            <CollapsibleContent>
              <div className="pt-2">
                <SessionFiltersComponent
                  filters={filters}
                  onFilterChange={setFilters}
                  activeFilterCount={activeFilterCount}
                />
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>

      {/* Group 1: Deine Verantwortung (Owner/Contributor) */}
      {ownedSessions.length > 0 && (
        <div className="rounded-2xl border border-primary/20 bg-card overflow-hidden">
          {/* Section header */}
          <div className="flex items-center gap-2 px-3 sm:px-4 py-2.5 border-b border-primary/10 bg-primary/[0.03]">
            <Shield className="h-3.5 w-3.5 text-primary" />
            <span className="text-xs sm:text-sm font-semibold text-foreground">Deine Verantwortung</span>
            <Badge variant="secondary" className="h-4 text-[9px] px-1.5 bg-primary/10 text-primary border-primary/20">
              {ownedSessions.length}
            </Badge>
          </div>

          {/* Table header - desktop only */}
          <div className="hidden sm:grid grid-cols-[1fr_100px_90px_120px_80px_80px] gap-2 px-4 py-2 text-[11px] font-medium text-muted-foreground uppercase tracking-wider border-b border-border/30 bg-muted/20">
            <span>Entscheidung</span>
            <span>Status</span>
            <span>Rolle</span>
            <span>Phase</span>
            <span>Alter</span>
            <span className="text-right">Aktion</span>
          </div>

          <div className="divide-y divide-border/30">
            {visibleOwned.map(session => (
              <SessionRow key={session.id} session={session} />
            ))}
          </div>

          {hasMoreOwned && (
            <div className="border-t border-border/30 px-4 py-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpandedOwned(!isExpandedOwned)}
                className="w-full text-xs text-muted-foreground hover:text-primary hover:bg-primary/5"
              >
                {isExpandedOwned ? (
                  <>Weniger anzeigen <ChevronUp className="h-3.5 w-3.5 ml-1" /></>
                ) : (
                  <>+{ownedSessions.length - maxVisiblePerGroup} weitere <ChevronDown className="h-3.5 w-3.5 ml-1" /></>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Group 2: Beteiligt (Reviewer/Viewer) */}
      {participantSessions.length > 0 && (
        <div className="rounded-2xl border border-border/50 bg-card overflow-hidden">
          {/* Section header */}
          <div className="flex items-center gap-2 px-3 sm:px-4 py-2.5 border-b border-border/30 bg-muted/10">
            <Eye className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs sm:text-sm font-medium text-muted-foreground">Beteiligt</span>
            <Badge variant="outline" className="h-4 text-[9px] px-1.5">
              {participantSessions.length}
            </Badge>
          </div>

          {/* Table header - desktop only */}
          <div className="hidden sm:grid grid-cols-[1fr_100px_90px_120px_80px_80px] gap-2 px-4 py-2 text-[11px] font-medium text-muted-foreground uppercase tracking-wider border-b border-border/30 bg-muted/20">
            <span>Entscheidung</span>
            <span>Status</span>
            <span>Rolle</span>
            <span>Phase</span>
            <span>Alter</span>
            <span className="text-right">Aktion</span>
          </div>

          <div className="divide-y divide-border/30">
            {visibleParticipant.map(session => (
              <SessionRow key={session.id} session={session} dimmed />
            ))}
          </div>

          {hasMoreParticipant && (
            <div className="border-t border-border/30 px-4 py-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsExpandedParticipant(!isExpandedParticipant)}
                className="w-full text-xs text-muted-foreground hover:text-primary hover:bg-primary/5"
              >
                {isExpandedParticipant ? (
                  <>Weniger anzeigen <ChevronUp className="h-3.5 w-3.5 ml-1" /></>
                ) : (
                  <>+{participantSessions.length - maxVisiblePerGroup} weitere <ChevronDown className="h-3.5 w-3.5 ml-1" /></>
                )}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Empty state when filters match nothing */}
      {filteredSessions.length === 0 && (
        <div className="rounded-2xl border border-border/50 bg-card p-6 text-center">
          <p className="text-sm text-muted-foreground">Keine Ergebnisse für aktuelle Filter.</p>
        </div>
      )}
    </div>
  );
};
